/**
 * Placeholders in the shape of what is about to appear (a page heading, a grid of cards, rows of a
 * list) instead of a spinner: the layout does not jump when the content arrives, and a list that
 * was seen before is shown from the client cache without any placeholder at all (lib/swr.ts).
 *
 * Decorative: screen readers get one polite "กำลังโหลด…" status, not a row of empty boxes.
 */
export function Skeleton({ width, height, radius, className = '' }: { width?: number | string; height?: number | string; radius?: number; className?: string }) {
  return <span className={`xc-skel ${className}`.trim()} aria-hidden="true" style={{ width, height, borderRadius: radius }} />;
}

/**
 * A grid of card placeholders (designs, files, templates). `grid` is the class of the real grid
 * (e.g. `xc-gallery`): the placeholders then sit in exactly the columns the cards will take.
 */
export function SkeletonGrid({ count = 8, ratio = '16 / 10', label = 'กำลังโหลด…', grid }: { count?: number; ratio?: string; label?: string; grid?: string }) {
  return (
    <div className={grid ?? 'xc-skel-grid'} role="status" aria-label={label}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="xc-skel-card" aria-hidden="true">
          <span className="xc-skel" style={{ aspectRatio: ratio }} />
          <span className="xc-skel xc-skel-line" style={{ width: `${70 - (i % 3) * 12}%` }} />
          <span className="xc-skel xc-skel-line is-short" />
          <span className="xc-skel xc-skel-line is-short" />
        </span>
      ))}
    </div>
  );
}

/** Rows of a list (tasks, chats, audit). */
export function SkeletonRows({ count = 6, label = 'กำลังโหลด…' }: { count?: number; label?: string }) {
  return (
    <div className="xc-skel-rows" role="status" aria-label={label}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="xc-skel xc-skel-row" aria-hidden="true" style={{ width: `${96 - (i % 4) * 9}%` }} />
      ))}
    </div>
  );
}

/** The inside of the app shell while a page's code is on its way. */
export function PageSkeleton() {
  return (
    <div className="xc-skel-page">
      <Skeleton className="xc-skel-title" />
      <Skeleton className="xc-skel-line" width="46%" />
      <SkeletonGrid count={8} />
    </div>
  );
}
